// frontend/src/components/staff/ScheduleBuilder.js
//
// Workforce — SCHED-SLICE1-E admin Schedule Builder page.
//
// Route: /staff/schedule (admin RBAC)
//
// Visual spec: docs/design/workforce-scheduling-slice-1-design.md §3 + §6
//              + §10 + §11.1 + §17 + §18.1 + §18.4 + §18.8 + §18.9
// Story:       docs/backlog/SCHED-SLICE1-E-admin-calendar-and-publish-ui.md
// Tech doc:    docs/architecture/workforce-scheduling-tech.md §"Admin
//              endpoints" + §"Real-time sync transport — POLLING"
//
// SCOPE — pure-additive new admin surface. No edits to existing pages.
// Pre-production posture per Kenya's git-boundary intent notice:
//   - Single-revert: removing this route + components removes the feature.
//   - SCHED-SLICE1-C publish + cancel endpoints live on main (PR #132).
//     The earlier `localStorage.SCHED_PUBLISH_DEV_FIXTURES` dev gate has
//     been retired; `publishSchedule` calls the BE directly.
//   - Polls `GET /api/workforce/shifts` at 30-sec cadence via
//     `useFocusedPoll` (codebase pattern; no TanStack Query here).
//   - FE-side availability HARD BLOCK policy via
//     `scheduleBuilderUtils.preValidateAvailability`.
//   - Weekly-cap WARN-WITH-CONFIRM modal per Q2 lock.
//   - ADMIN MODE chrome bar persistent.
//   - No labor-cost / pay-rate rendered anywhere.

import React from "react";
import { workforceShiftsAPI } from "../../services/api";
import { useFocusedPoll } from "../../hooks/useFocusedPoll";
import ScheduleBuilderToolbar from "./ScheduleBuilderToolbar";
import ScheduleBuilderGrid from "./ScheduleBuilderGrid";
import EditShiftModal from "./EditShiftModal";
import WeeklyHourLimitModal from "./WeeklyHourLimitModal";
import PublishScheduleModal from "./PublishScheduleModal";
import CancelShiftModal from "./CancelShiftModal";
import CancelledShiftDetailModal from "./CancelledShiftDetailModal";
import WeeklyHourSummaryFooter from "./WeeklyHourSummaryFooter";
import {
  applyOptimisticShiftMutation,
  buildShiftsSignature,
  computePublishCtaState,
  computeWeekRange,
  computeWeekStart,
  computeWeeklyHourSummary,
  dayAndMinuteToIso,
  formatRoleLabel,
  formatWeekRangeLabel,
  indexStaffById,
  preValidateAvailability,
  resolveStaffDisplayName,
  stepWeek,
} from "./scheduleBuilderUtils";
import "./ScheduleBuilder.css";

const POLL_INTERVAL_MS = 30 * 1000; // 30 seconds (tech doc §"Real-time sync — POLLING")

// Lightweight toast helper — uses the existing react-toastify imported
// at the App.js level; avoids a new dependency.
let toastFn = null;
try {
  // eslint-disable-next-line global-require
  toastFn = require("react-toastify").toast;
} catch (e) {
  toastFn = null;
}

const showToast = (message, variant) => {
  if (toastFn) {
    if (variant === "error") toastFn.error(message);
    else if (variant === "success") toastFn.success(message);
    else toastFn(message);
  }
};

const ScheduleBuilder = () => {
  // ---- Week / URL-derived state -------------------------------------------
  const [weekStartYmd, setWeekStartYmd] = React.useState(() => computeWeekStart());

  // ---- Data state ---------------------------------------------------------
  const [shifts, setShifts] = React.useState([]);
  const [staff, setStaff] = React.useState([]);
  const [availability, setAvailability] = React.useState([]);
  const [, setIsLoading] = React.useState(false);
  const [isInitialLoad, setIsInitialLoad] = React.useState(true);
  const [loadError, setLoadError] = React.useState(null);
  // Stable signature of the visible-week shifts list — used by polling
  // to detect background changes and surface a subtle toast. Kept in
  // setter form (no read on the JSX side).
  const [, setLastSignature] = React.useState("");

  // ---- Filter state -------------------------------------------------------
  const [filterStaffProfileId, setFilterStaffProfileId] = React.useState(null);

  // ---- Modal state --------------------------------------------------------
  // Edit/New shift modal.
  const [shiftModal, setShiftModal] = React.useState({
    open: false,
    mode: "new",
    initialShift: null,
    isSubmitting: false,
    serverError: null,
  });
  // Pending weekly-hour-cap warn modal.
  const [whModal, setWhModal] = React.useState({
    open: false,
    weeklyHourLimit: null,
    staffName: "",
    pendingShift: null,
    isSubmitting: false,
  });
  // Publish modal.
  const [publishModal, setPublishModal] = React.useState({
    open: false,
    isSubmitting: false,
    error: null,
    pendingConflicts: null,
  });
  // Cancel-published-shift modal (SCHED-SLICE1-C). Opened from
  // EditShiftModal's delete affordance when the shift is published.
  const [cancelShiftModal, setCancelShiftModal] = React.useState({
    open: false,
    shift: null,
    isSubmitting: false,
    error: null,
  });
  // SCHED-CANCELLED-VISUAL-ADMIN — read-only Cancelled-detail modal opened
  // when admin clicks a cancelled shift on the grid. Separate from the
  // edit modal because cancelled shifts cannot be edited (terminal state).
  const [cancelledDetailModal, setCancelledDetailModal] = React.useState({
    open: false,
    shift: null,
  });

  // ---- Conflict highlighting (visual cue for rejected drop) ---------------
  const [conflictHighlightShiftId, setConflictHighlightShiftId] = React.useState(null);
  React.useEffect(() => {
    if (!conflictHighlightShiftId) return undefined;
    const t = setTimeout(() => setConflictHighlightShiftId(null), 300);
    return () => clearTimeout(t);
  }, [conflictHighlightShiftId]);

  // ---- Indices ------------------------------------------------------------
  const staffById = React.useMemo(() => indexStaffById(staff), [staff]);
  const weekRange = React.useMemo(() => computeWeekRange(weekStartYmd), [weekStartYmd]);
  const weekDays = React.useMemo(() => {
    // weekDays for the day-picker in the modal mirrors the rendered week.
    // Reuse buildWeekDays via the grid path — here we build a flat list.
    const list = [];
    const start = new Date(`${weekStartYmd}T00:00:00`);
    for (let i = 0; i < 7; i++) {
      const d = new Date(start.getTime() + i * 24 * 60 * 60 * 1000);
      list.push({
        iso: d.toISOString().slice(0, 10),
        weekday: d.toLocaleDateString("en-US", { weekday: "short" }),
        monthLabel: d.toLocaleDateString("en-US", { month: "short" }),
        dayLabel: d.getDate(),
      });
    }
    return list;
  }, [weekStartYmd]);
  const weekRangeLabel = React.useMemo(() => formatWeekRangeLabel(weekStartYmd), [weekStartYmd]);

  // ---- Fetch helper -------------------------------------------------------
  const fetchShifts = React.useCallback(
    async (markInitial = false) => {
      if (markInitial) setIsInitialLoad(true);
      setIsLoading(true);
      setLoadError(null);
      try {
        const res = await workforceShiftsAPI.listShifts({
          from: weekRange.fromIso,
          to: weekRange.toIso,
        });
        const data = res && res.data ? res.data : {};
        const nextShifts = Array.isArray(data.shifts) ? data.shifts : [];
        const nextStaff = Array.isArray(data.staff) ? data.staff : [];
        const nextAvailability = Array.isArray(data.availability) ? data.availability : [];
        setShifts(nextShifts);
        setStaff(nextStaff);
        setAvailability(nextAvailability);
        const nextSig = buildShiftsSignature(nextShifts);
        setLastSignature((prev) => {
          // First load establishes baseline; subsequent polling-detected
          // change triggers a subtle toast informing the admin.
          if (!markInitial && prev && prev !== nextSig) {
            showToast("Schedule updated by another admin", "info");
          }
          return nextSig;
        });
      } catch (e) {
        setLoadError(e);
      } finally {
        setIsLoading(false);
        if (markInitial) setIsInitialLoad(false);
      }
    },
    [weekRange.fromIso, weekRange.toIso]
  );

  // ---- Initial load + on-week-change refetch ------------------------------
  React.useEffect(() => {
    fetchShifts(true);
  }, [fetchShifts]);

  // ---- Polling ------------------------------------------------------------
  useFocusedPoll(() => fetchShifts(false), POLL_INTERVAL_MS, [weekRange.fromIso, weekRange.toIso]);

  // ---- Week nav -----------------------------------------------------------
  const onPrevWeek = React.useCallback(() => setWeekStartYmd((w) => stepWeek(w, -1)), []);
  const onNextWeek = React.useCallback(() => setWeekStartYmd((w) => stepWeek(w, +1)), []);
  const onToday = React.useCallback(() => setWeekStartYmd(computeWeekStart()), []);
  const onWeekStartChange = React.useCallback(
    (ymd) => setWeekStartYmd(computeWeekStart(ymd)),
    []
  );

  // ---- Cell-click + drag-create handlers ----------------------------------
  const openNewShiftModal = React.useCallback(
    (dayIso, startMin, endMin) => {
      const startIso = dayAndMinuteToIso(dayIso, startMin);
      const endIso = dayAndMinuteToIso(dayIso, endMin);
      setShiftModal({
        open: true,
        mode: "new",
        initialShift: { startTime: startIso, endTime: endIso, status: "draft" },
        isSubmitting: false,
        serverError: null,
      });
    },
    []
  );

  const handleCellClick = ({ dayIso, minuteOfDay }) => {
    openNewShiftModal(dayIso, minuteOfDay, minuteOfDay + 60);
  };
  const handleCellDragCreate = ({ dayIso, startMin, endMin }) => {
    openNewShiftModal(dayIso, startMin, endMin);
  };

  const handleShiftClick = (shift) => {
    // SCHED-CANCELLED-VISUAL-ADMIN — cancelled shifts open a read-only
    // detail modal, not the editable EditShiftModal. Cancelled is the
    // terminal state per Shift.js state-machine; the modal carries the
    // forensic metadata (reason, cancelledAt, cancelledBy) and a notice
    // banner explaining the un-cancel path (cancel-and-recreate).
    if (shift && shift.status === "cancelled") {
      setCancelledDetailModal({ open: true, shift });
      return;
    }
    setShiftModal({
      open: true,
      mode: "edit",
      initialShift: shift,
      isSubmitting: false,
      serverError: null,
    });
  };

  const handleCancelledDetailClose = React.useCallback(() => {
    setCancelledDetailModal({ open: false, shift: null });
  }, []);

  // SCHED-CANCELLED-VISUAL-ADMIN — derive staff + role display for the
  // detail modal header. Memoized so the modal does not re-render on
  // every parent setState.
  const cancelledDetailDisplay = React.useMemo(() => {
    const s = cancelledDetailModal.shift;
    if (!s) return { staffDisplayName: "", staffRoleLabel: "" };
    const profile = staffById && staffById.get ? staffById.get(String(s.staffProfileId)) : null;
    return {
      staffDisplayName: resolveStaffDisplayName(profile),
      staffRoleLabel: formatRoleLabel(s.roleCode),
    };
  }, [cancelledDetailModal.shift, staffById]);

  // ---- Drag-move (PUT /api/workforce/shifts/:id) --------------------------
  const handleShiftDragMove = async ({ shift, dayIso, startMin, endMin }) => {
    const newStartIso = dayAndMinuteToIso(dayIso, startMin);
    const newEndIso = dayAndMinuteToIso(dayIso, endMin);

    // FE-side HARD BLOCK: pre-validate against the loaded `availability`.
    const conflict = preValidateAvailability({
      staffProfileId: shift.staffProfileId,
      startIso: newStartIso,
      endIso: newEndIso,
      availability,
    });
    if (conflict) {
      setConflictHighlightShiftId(shift._id);
      showToast(conflict.message, "error");
      return;
    }

    try {
      const res = await workforceShiftsAPI.updateShift(shift._id, {
        startTime: newStartIso,
        endTime: newEndIso,
      });
      const updated = res && res.data && res.data.shift ? res.data.shift : null;
      const conflicts = res && res.data && res.data.conflicts ? res.data.conflicts : null;
      if (updated) {
        setShifts((prev) =>
          applyOptimisticShiftMutation(prev, { type: "update", shift: updated })
        );
      }
      // Weekly-cap WARN-WITH-CONFIRM modal if BE flags overage.
      if (conflicts && conflicts.weeklyHourLimit) {
        const profile = staffById.get(String(shift.staffProfileId));
        setWhModal({
          open: true,
          weeklyHourLimit: conflicts.weeklyHourLimit,
          staffName: resolveStaffDisplayName(profile),
          pendingShift: updated || shift,
          isSubmitting: false,
        });
      }
    } catch (e) {
      const msg =
        (e.response && e.response.data && e.response.data.message) || "Couldn't move shift.";
      showToast(msg, "error");
    }
  };

  // ---- Save (POST or PUT) -------------------------------------------------
  const handleShiftSave = async (payload) => {
    setShiftModal((m) => ({ ...m, isSubmitting: true, serverError: null }));
    try {
      const isEdit = shiftModal.mode === "edit" && shiftModal.initialShift && shiftModal.initialShift._id;
      const res = isEdit
        ? await workforceShiftsAPI.updateShift(shiftModal.initialShift._id, payload)
        : await workforceShiftsAPI.createShift(payload);
      const newShift = res && res.data && res.data.shift ? res.data.shift : null;
      const conflicts = res && res.data && res.data.conflicts ? res.data.conflicts : null;
      if (newShift) {
        setShifts((prev) =>
          applyOptimisticShiftMutation(prev, {
            type: isEdit ? "update" : "create",
            shift: newShift,
          })
        );
      }
      setShiftModal({ open: false, mode: "new", initialShift: null, isSubmitting: false, serverError: null });

      // Weekly-cap WARN-WITH-CONFIRM if BE flagged.
      if (conflicts && conflicts.weeklyHourLimit) {
        const profile = staffById.get(String(payload.staffProfileId));
        setWhModal({
          open: true,
          weeklyHourLimit: conflicts.weeklyHourLimit,
          staffName: resolveStaffDisplayName(profile),
          pendingShift: newShift,
          isSubmitting: false,
        });
      } else {
        showToast(isEdit ? "Shift updated" : "Shift created", "success");
      }
    } catch (e) {
      const msg =
        (e.response && e.response.data && e.response.data.message) || "Couldn't save shift.";
      setShiftModal((m) => ({ ...m, isSubmitting: false, serverError: msg }));
    }
  };

  // ---- Delete ------------------------------------------------------------
  const handleShiftDelete = async (shiftId) => {
    setShiftModal((m) => ({ ...m, isSubmitting: true, serverError: null }));
    try {
      await workforceShiftsAPI.deleteShift(shiftId);
      setShifts((prev) => applyOptimisticShiftMutation(prev, { type: "delete", shiftId }));
      setShiftModal({ open: false, mode: "new", initialShift: null, isSubmitting: false, serverError: null });
      showToast("Shift deleted", "success");
    } catch (e) {
      const msg =
        (e.response && e.response.data && e.response.data.message) || "Couldn't delete shift.";
      setShiftModal((m) => ({ ...m, isSubmitting: false, serverError: msg }));
    }
  };

  // ---- Weekly hour modal confirm/cancel -----------------------------------
  const handleWhConfirm = async ({ reason }) => {
    // Acknowledged. Persist the override-reason to the BE via a PUT.
    // SCHED-1B accepts updates with the same fields; we send no field change
    // beyond a notes-append (visual spec §10.2 logs to AuditLog metadata via
    // Story C BE — Slice 1 FE captures the reason on local state and routes
    // through the next mutation. Until SCHED-1C BE lands, the reason is
    // surfaced via a follow-up notes update.)
    const pending = whModal.pendingShift;
    if (!pending || !pending._id || !reason) {
      setWhModal({ open: false, weeklyHourLimit: null, staffName: "", pendingShift: null, isSubmitting: false });
      return;
    }
    setWhModal((m) => ({ ...m, isSubmitting: true }));
    try {
      const prior = pending.notes ? `${pending.notes}\n` : "";
      const next = `${prior}[Weekly-limit override] ${reason}`;
      await workforceShiftsAPI.updateShift(pending._id, { notes: next });
      setShifts((prev) =>
        applyOptimisticShiftMutation(prev, {
          type: "update",
          shift: { ...pending, notes: next },
        })
      );
    } catch (e) {
      // Non-blocking; the shift itself is already saved.
      showToast("Couldn't record override reason; shift assignment kept.", "error");
    } finally {
      setWhModal({ open: false, weeklyHourLimit: null, staffName: "", pendingShift: null, isSubmitting: false });
    }
  };

  const handleWhCancel = async () => {
    // Cancel = revert the just-created shift (visual spec §10.2 "On Cancel,
    // the just-created draft shift is deleted").
    const pending = whModal.pendingShift;
    setWhModal({ open: false, weeklyHourLimit: null, staffName: "", pendingShift: null, isSubmitting: false });
    if (pending && pending._id && pending.status === "draft") {
      try {
        await workforceShiftsAPI.deleteShift(pending._id);
        setShifts((prev) =>
          applyOptimisticShiftMutation(prev, { type: "delete", shiftId: pending._id })
        );
      } catch (e) {
        // Best-effort cleanup; surface a toast and let the next poll catch up.
        showToast("Couldn't roll back over-cap shift; review manually.", "error");
      }
    }
  };

  // ---- Cancel published shift workflow (SCHED-SLICE1-C) ------------------
  // From EditShiftModal: when admin clicks "Cancel shift" on a published
  // shift, close the edit modal and open the cancel-with-reason sub-modal.
  // (The two modals are sequenced — closing the edit modal first keeps the
  // backdrop / z-index management simple.)
  const handleCancelPublishedRequest = (shift) => {
    setShiftModal({ open: false, mode: "new", initialShift: null, isSubmitting: false, serverError: null });
    setCancelShiftModal({ open: true, shift, isSubmitting: false, error: null });
  };

  const handleCancelShiftConfirm = async ({ reason }) => {
    const target = cancelShiftModal.shift;
    if (!target || !target._id) {
      setCancelShiftModal({ open: false, shift: null, isSubmitting: false, error: null });
      return;
    }
    setCancelShiftModal((m) => ({ ...m, isSubmitting: true, error: null }));
    try {
      const res = await workforceShiftsAPI.cancelShift(target._id, { reason });
      const updated = res && res.data && res.data.shift ? res.data.shift : null;
      if (updated) {
        setShifts((prev) =>
          applyOptimisticShiftMutation(prev, { type: "update", shift: updated })
        );
      }
      setCancelShiftModal({ open: false, shift: null, isSubmitting: false, error: null });
      showToast("Shift cancelled. Staff member notified.", "success");
      // Defensive refetch — keeps the page in sync with whatever the BE
      // committed (cancelledAt timestamp, audit metadata, etc.).
      fetchShifts(false);
    } catch (e) {
      const code = e.response && e.response.data && e.response.data.code;
      const message =
        (e.response && e.response.data && e.response.data.message) ||
        "Couldn't cancel shift.";
      // SHIFT_ALREADY_CANCELLED is a terminal-state guard — surface the
      // BE message inline AND refresh so the FE catches up. Refusing
      // the re-cancel is the intended UX per AC Scenario 9.
      if (code === "SHIFT_ALREADY_CANCELLED") {
        setCancelShiftModal((m) => ({
          ...m,
          isSubmitting: false,
          error: "This shift is already cancelled. Refreshing.",
        }));
        fetchShifts(false);
      } else {
        setCancelShiftModal((m) => ({
          ...m,
          isSubmitting: false,
          error: message,
        }));
      }
    }
  };

  const handleCancelShiftClose = () => {
    if (cancelShiftModal.isSubmitting) return;
    setCancelShiftModal({ open: false, shift: null, isSubmitting: false, error: null });
  };

  // Build a display-friendly shape for the cancel sub-modal.
  const cancelShiftDisplay = React.useMemo(() => {
    const s = cancelShiftModal.shift;
    if (!s) return { staffDisplayName: "", shiftWithLabel: null };
    const profile = staffById.get(String(s.staffProfileId));
    return {
      staffDisplayName: profile ? resolveStaffDisplayName(profile) : "Unknown staff",
      shiftWithLabel: { ...s, roleLabel: s.roleCode ? formatRoleLabel(s.roleCode) : null },
    };
  }, [cancelShiftModal.shift, staffById]);

  // ---- Publish workflow ---------------------------------------------------
  const publishCta = React.useMemo(() => computePublishCtaState(shifts), [shifts]);

  const handlePublishClick = () => {
    setPublishModal({ open: true, isSubmitting: false, error: null, pendingConflicts: null });
  };

  const handlePublishConfirm = async ({ confirmConflicts }) => {
    setPublishModal((m) => ({ ...m, isSubmitting: true, error: null }));
    const draftIds = shifts.filter((s) => s.status === "draft").map((s) => s._id);
    try {
      await workforceShiftsAPI.publishSchedule({
        from: weekRange.fromIso,
        to: weekRange.toIso,
        shiftIds: draftIds,
        confirmConflicts: !!confirmConflicts,
      });
      setPublishModal({ open: false, isSubmitting: false, error: null, pendingConflicts: null });
      showToast(
        `Schedule published for ${weekRangeLabel}. ${draftIds.length} shift${
          draftIds.length === 1 ? "" : "s"
        } notified.`,
        "success"
      );
      // Refetch to pick up the BE's published-status flips.
      fetchShifts(false);
    } catch (e) {
      const code = e.response && e.response.data && e.response.data.code;
      const message = (e.response && e.response.data && e.response.data.message) || "Couldn't publish schedule.";
      if (code === "PUBLISH_CONFIRM_REQUIRED") {
        const conflicts = e.response.data.conflicts || null;
        setPublishModal((m) => ({
          ...m,
          isSubmitting: false,
          error: "Conflicts detected. Review and confirm to publish anyway.",
          pendingConflicts: conflicts,
        }));
      } else if (code === "NO_SHIFTS_TO_PUBLISH") {
        setPublishModal({ open: false, isSubmitting: false, error: null, pendingConflicts: null });
        showToast("No draft shifts to publish in this week.", "info");
      } else {
        setPublishModal((m) => ({
          ...m,
          isSubmitting: false,
          error: message,
        }));
      }
    }
  };

  // ---- Derived UI surface --------------------------------------------------
  const summary = React.useMemo(
    () => computeWeeklyHourSummary(shifts, staffById),
    [shifts, staffById]
  );

  const filterStaffName = React.useMemo(() => {
    if (!filterStaffProfileId) return null;
    const profile = staffById.get(String(filterStaffProfileId));
    return profile ? resolveStaffDisplayName(profile) : null;
  }, [filterStaffProfileId, staffById]);

  const auditLinkHref = `/staff/schedule/audit?weekStart=${weekStartYmd}`;

  const isEmpty = !isInitialLoad && !loadError && shifts.length === 0;

  return (
    <div className="schedule-builder" data-testid="schedule-builder">
      <ScheduleBuilderToolbar
        weekStartYmd={weekStartYmd}
        weekRangeLabel={weekRangeLabel}
        publishCta={publishCta}
        onPrevWeek={onPrevWeek}
        onNextWeek={onNextWeek}
        onToday={onToday}
        onWeekStartChange={onWeekStartChange}
        onPublishClick={handlePublishClick}
        auditLinkHref={auditLinkHref}
        staffFilterLabel={filterStaffName}
        onClearStaffFilter={() => setFilterStaffProfileId(null)}
      />

      {loadError && (
        <div
          className="schedule-builder__error-banner"
          role="alert"
          data-testid="schedule-builder-error-banner"
        >
          Couldn't load the schedule. {" "}
          <button
            type="button"
            className="schedule-builder__error-refresh"
            onClick={() => fetchShifts(true)}
          >
            Refresh
          </button>
          {" "}to try again.
        </div>
      )}

      {isInitialLoad && (
        <div className="schedule-builder__loading" role="status">
          Loading schedule...
        </div>
      )}

      {!isInitialLoad && (
        <ScheduleBuilderGrid
          weekStartYmd={weekStartYmd}
          shifts={shifts}
          staffById={staffById}
          filterStaffProfileId={filterStaffProfileId}
          onCellClick={handleCellClick}
          onCellDragCreate={handleCellDragCreate}
          onShiftClick={handleShiftClick}
          onShiftDragMove={handleShiftDragMove}
          conflictHighlightShiftId={conflictHighlightShiftId}
          isEmpty={isEmpty}
        />
      )}

      <WeeklyHourSummaryFooter
        summary={summary}
        filterStaffProfileId={filterStaffProfileId}
        onStaffSelect={setFilterStaffProfileId}
      />

      <EditShiftModal
        open={shiftModal.open}
        mode={shiftModal.mode}
        initialShift={shiftModal.initialShift}
        weekDays={weekDays}
        staff={staff}
        availability={availability}
        /* SCHED-SAME-ROLE-OVERLAP-WARN — supply the visible-week shifts +
           staff-by-id map so the modal can run client-side overlap
           detection without a BE round-trip. */
        shifts={shifts}
        staffById={staffById}
        onSave={handleShiftSave}
        onDelete={handleShiftDelete}
        onCancelPublished={handleCancelPublishedRequest}
        /* SCHED-SLICE1-K — refetch listShifts (which returns the
           availability list inline) so the conflict-warning panel
           resolves immediately on a successful admin-set, instead of
           waiting for the 30-sec poll. */
        onAvailabilityChanged={() => fetchShifts(false)}
        onClose={() =>
          setShiftModal({ open: false, mode: "new", initialShift: null, isSubmitting: false, serverError: null })
        }
        isSubmitting={shiftModal.isSubmitting}
        serverErrorMessage={shiftModal.serverError}
      />

      <WeeklyHourLimitModal
        open={whModal.open}
        staffName={whModal.staffName}
        weeklyHourLimit={whModal.weeklyHourLimit}
        onConfirm={handleWhConfirm}
        onCancel={handleWhCancel}
        isSubmitting={whModal.isSubmitting}
      />

      <PublishScheduleModal
        open={publishModal.open}
        weekRangeLabel={weekRangeLabel}
        draftShifts={shifts.filter((s) => s.status === "draft")}
        staffById={staffById}
        pendingConflicts={publishModal.pendingConflicts}
        onConfirm={handlePublishConfirm}
        onCancel={() =>
          setPublishModal({ open: false, isSubmitting: false, error: null, pendingConflicts: null })
        }
        isSubmitting={publishModal.isSubmitting}
        errorMessage={publishModal.error}
      />

      <CancelShiftModal
        open={cancelShiftModal.open}
        shift={cancelShiftDisplay.shiftWithLabel}
        staffDisplayName={cancelShiftDisplay.staffDisplayName}
        onConfirm={handleCancelShiftConfirm}
        onCancel={handleCancelShiftClose}
        isSubmitting={cancelShiftModal.isSubmitting}
        errorMessage={cancelShiftModal.error}
      />

      <CancelledShiftDetailModal
        open={cancelledDetailModal.open}
        shift={cancelledDetailModal.shift}
        staffDisplayName={cancelledDetailDisplay.staffDisplayName}
        staffRoleLabel={cancelledDetailDisplay.staffRoleLabel}
        onClose={handleCancelledDetailClose}
      />
    </div>
  );
};

export default ScheduleBuilder;
